import { useEffect, useRef } from "react";

/**
 * Locks navigation while `active` is true so a long-running task (e.g. an
 * Excel import) cannot be silently abandoned.
 *
 * What it covers:
 *  - `beforeunload` — confirms tab close / refresh / external link.
 *  - Capture-phase click interceptor — confirms internal anchor clicks
 *    (sidebar `NavLink`s render `<a href="...">`, so this catches them).
 *  - `popstate` — confirms back/forward; if the user cancels we re-push the
 *    locked URL so the page they're on stays visible.
 *
 * Why a custom hook? `react-router-dom` v6 only ships `useBlocker` for data
 * routers (`createBrowserRouter`). This app boots from `<BrowserRouter>` in
 * `main.tsx`, so we guard at the DOM level instead.
 *
 * Always pair with a `try/finally` that flips `active` back to `false`, so an
 * error during the locked work doesn't strand the user with navigation
 * permanently blocked.
 */
export function useNavigationLock(active: boolean, message: string): void {
  const messageRef = useRef(message);
  messageRef.current = message;

  useEffect(() => {
    if (!active) return;

    const lockedUrl =
      window.location.pathname + window.location.search + window.location.hash;

    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the custom string but still need returnValue
      // set to trigger the native confirmation prompt.
      e.returnValue = messageRef.current;
      return messageRef.current;
    };

    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      const anchor = target?.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;

      const rawHref = anchor.getAttribute("href");
      if (!rawHref) return;
      if (anchor.target && anchor.target !== "" && anchor.target !== "_self") {
        return;
      }
      if (
        rawHref.startsWith("mailto:") ||
        rawHref.startsWith("tel:") ||
        rawHref.startsWith("#")
      ) {
        return;
      }

      let nextUrl: URL;
      try {
        nextUrl = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      // Cross-origin → let the browser handle it (beforeunload will fire).
      if (nextUrl.origin !== window.location.origin) return;
      // Same path/search → not a navigation, leave it alone.
      if (
        nextUrl.pathname === window.location.pathname &&
        nextUrl.search === window.location.search
      ) {
        return;
      }

      const proceed = window.confirm(messageRef.current);
      if (!proceed) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    const onPopState = () => {
      const proceed = window.confirm(messageRef.current);
      if (!proceed) {
        // Re-pin the user to the URL where the lock was activated. We can't
        // perfectly reverse a popstate, but pushing the original URL keeps
        // the visible page in sync with the route.
        window.history.pushState(null, "", lockedUrl);
      }
    };

    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", onClickCapture, true);
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, [active]);
}
